import { useState, useEffect, useRef } from 'react';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { CustomDialog } from 'Components/CustomDialog';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { getFileMimeType } from 'Utils/getFileMimeType';
import { downloadFile } from 'Utils/downloadFile';
import { useLoader } from 'Hooks/useLoader';
import { useIsDesktop } from 'Hooks';
import {
  LeftArrowWhite,
  Download2White,
  GreenCheckMark,
  ZoomIn,
  ZoomOut,
} from 'Images';

type PdfPreviewComponents = {
  Document: typeof import('react-pdf').Document;
  Page: typeof import('react-pdf').Page;
};

interface IFileViewerDialog {
  showDialog: boolean;
  onClose: () => void;
  base64: string;
  fileExtension: string;
  fileName?: string;
  /** When set, called on each download click so the name can include a fresh timestamp. */
  resolveDownloadFileName?: () => string;
  title?: string;
  showDownload?: boolean;
  showZoom?: boolean;
  maxDialogWidth?: string | Record<string, string | number>;
  maxDialogHeight?: string | Record<string, string | number>;
  zIndex?: number;
}

const DOWNLOAD_SUCCESS_DISPLAY_MS = 5000;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.25;

const isPdfExtension = (ext: string) => ext.toLowerCase() === 'pdf';

const isImageExtension = (ext: string) => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
  return imageExtensions.includes(ext.toLowerCase());
};

const UNSUPPORTED_IMAGE_EXTENSIONS = ['heic', 'heif'];

const isPreviewableImage = (ext: string) =>
  isImageExtension(ext) &&
  !UNSUPPORTED_IMAGE_EXTENSIONS.includes(ext.toLowerCase());

const DEFAULT_DIALOG_WIDTH: string = '90%';
const DEFAULT_DIALOG_HEIGHT: string = '90vh';

export const FileViewerDialog = ({
  showDialog,
  onClose,
  base64,
  fileExtension,
  fileName = 'document',
  resolveDownloadFileName,
  title,
  showDownload = true,
  showZoom = false,
  maxDialogWidth = DEFAULT_DIALOG_WIDTH,
  maxDialogHeight = DEFAULT_DIALOG_HEIGHT,
  zIndex,
}: IFileViewerDialog) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'success'>(
    'idle',
  );
  const [zoom, setZoom] = useState(1);
  const { loader, setIsLoading, isLoading } = useLoader();
  const {
    loader: downloadLoader,
    setIsLoading: setIsDownloading,
    isLoading: isDownloading,
  } = useLoader('20', { width: '100px' }, 20, false);

  const isDesktop = useIsDesktop();
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentWidth, setContentWidth] = useState<number | undefined>();
  const mimeType = getFileMimeType(fileExtension);
  const isPdf = isPdfExtension(fileExtension);
  const isImage = isPreviewableImage(fileExtension);
  const [imageLoadError, setImageLoadError] = useState(false);
  const [pdfComponents, setPdfComponents] =
    useState<PdfPreviewComponents | null>(null);

  useEffect(() => {
    if (!showDialog || !isPdf) return;

    let cancelled = false;
    setIsLoading(true);

    Promise.all([
      import('react-pdf'),
      import('react-pdf/dist/Page/AnnotationLayer.css'),
      import('react-pdf/dist/Page/TextLayer.css'),
    ])
      .then(([reactPdf]) => {
        if (cancelled) return;

        reactPdf.pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.mjs',
          import.meta.url,
        ).toString();
        setPdfComponents({
          Document: reactPdf.Document,
          Page: reactPdf.Page,
        });
      })
      .catch(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [showDialog, isPdf, setIsLoading]);

  useEffect(() => {
    if (!showDialog || !contentRef.current) return;
    const el = contentRef.current;
    const updateWidth = () => setContentWidth(el.offsetWidth);
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showDialog, isPdf]);

  const handleZoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP));
  const handleZoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP));

  useEffect(() => {
    if (showDialog) {
      setZoom(1);
      setImageLoadError(false);
    }
  }, [showDialog, base64]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setIsLoading(false);
  };

  const onDocumentLoadError = () => {
    setIsLoading(false);
  };

  const handlePrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  };

  const handleDialogClose = () => {
    setPageNumber(1);
    setNumPages(0);
    setDownloadStatus('idle');
    setZoom(1);
    setImageLoadError(false);
    onClose();
  };

  const handleDownload = () => {
    setIsDownloading(true);
    try {
      const resolvedName = resolveDownloadFileName?.() ?? fileName;
      downloadFile(
        base64,
        fileExtension,
        resolvedName,
        true,
        Boolean(resolveDownloadFileName),
      );
      setIsDownloading(false);
      setDownloadStatus('success');
      setTimeout(() => setDownloadStatus('idle'), DOWNLOAD_SUCCESS_DISPLAY_MS);
    } catch {
      setIsDownloading(false);
    }
  };

  const isZoomable = isPdf || isImage;

  const renderZoomableContent = (content: React.ReactNode) => {
    if (!showZoom) return content;
    return (
      <Box
        sx={{
          transform: `scale(${zoom})`,
          transformOrigin: 'center center',
          transition: 'transform 0.2s ease',
        }}
      >
        {content}
      </Box>
    );
  };

  return (
    <CustomDialog
      showDialog={showDialog}
      onclose={handleDialogClose}
      width={maxDialogWidth}
      height={maxDialogHeight}
      paperSx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      forceHighZIndex={!zIndex}
      zIndex={zIndex}
    >
      {title && (
        <Stack
          sx={{
            padding: isDesktop ? '16px 24px' : '12px 16px',
            mt: isDesktop ? '60px' : '50px',
          }}
        >
          <Typography
            sx={{
              fontSize: isDesktop ? '22px !important' : '18px !important',
              fontWeight: '600 !important',
              lineHeight: '28px !important',
              color: '#344266',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              alignSelf: isDesktop ? 'center' : 'flex-start',
              maxWidth: '100%',
            }}
            title={title}
          >
            {title}
          </Typography>
        </Stack>
      )}

      <Box
        ref={contentRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: title ? 2 : '75px',
          pb: 2,
          px: 2,
          backgroundColor: undefined,
        }}
      >
        {isPdf && (
          <>
            {(isLoading || !pdfComponents) && loader}
            {pdfComponents &&
              (() => {
                const { Document: PdfDocument, Page: PdfPage } = pdfComponents;
                return renderZoomableContent(
                  <PdfDocument
                    file={`data:application/pdf;base64,${base64}`}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading=''
                  >
                    <PdfPage
                      pageNumber={pageNumber}
                      renderTextLayer
                      renderAnnotationLayer
                      width={
                        contentWidth != null
                          ? contentWidth
                          : window.innerWidth * 0.9 * 0.85
                      }
                    />
                  </PdfDocument>,
                );
              })()}
            {numPages > 1 && (
              <Stack
                direction='row'
                alignItems='center'
                sx={{ mt: 2, pb: 1, justifyContent: 'center', gap: '16px' }}
              >
                <IconButton
                  onClick={handlePrevPage}
                  disabled={pageNumber <= 1}
                  sx={{
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&:disabled': { bgcolor: 'grey.300' },
                    width: '30px',
                    height: '30px',
                  }}
                >
                  <Avatar
                    src={LeftArrowWhite}
                    alt=''
                    sx={{
                      width: '16px',
                      height: '16px',
                      transform: 'rotate(180deg)',
                    }}
                  />
                </IconButton>
                <Typography>
                  {pageNumber} / {numPages}
                </Typography>
                <IconButton
                  onClick={handleNextPage}
                  disabled={pageNumber >= numPages}
                  sx={{
                    bgcolor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&:disabled': { bgcolor: 'grey.300' },
                    width: '30px',
                    height: '30px',
                  }}
                >
                  <Avatar
                    src={LeftArrowWhite}
                    alt=''
                    sx={{
                      width: '16px',
                      height: '16px',
                    }}
                  />
                </IconButton>
              </Stack>
            )}
          </>
        )}

        {isImage &&
          !imageLoadError &&
          renderZoomableContent(
            <Box
              component='img'
              src={`data:${mimeType};base64,${base64}`}
              alt={Dictionary[DICTIONARY.General].watch}
              onError={() => setImageLoadError(true)}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />,
          )}

        {((!isPdf && !isImage) || imageLoadError) && (
          <Stack alignItems='center' spacing={2} sx={{ mt: 4 }}>
            <Typography color='text.secondary'>
              {Dictionary[DICTIONARY.General].previewNotAvailable}
            </Typography>
          </Stack>
        )}

        {showDownload && (
          <Box sx={{ mt: 2, minHeight: '44px' }}>
            {isDownloading ? (
              downloadLoader
            ) : downloadStatus === 'success' ? (
              <Stack direction='row' alignItems='center' gap={1}>
                <Avatar
                  src={GreenCheckMark}
                  alt=''
                  sx={{ width: '20px', height: '20px' }}
                />
                <Typography variant='body6' color='success.main'>
                  {Dictionary[DICTIONARY.Documents].downloadSuccess}
                </Typography>
              </Stack>
            ) : (
              <Button
                onClick={handleDownload}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  borderRadius: '22px',
                  px: 3,
                  py: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <Avatar
                  src={Download2White}
                  alt=''
                  sx={{ width: '20px', height: '20px' }}
                />
                <Typography variant='body6' color='white'>
                  {Dictionary[DICTIONARY.Documents].download}
                </Typography>
              </Button>
            )}
          </Box>
        )}
      </Box>

      {showZoom && isZoomable && isDesktop && (
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='center'
          gap={0.5}
          sx={{
            padding: isDesktop ? '12px 24px' : '10px 16px',
            backgroundColor: undefined,
          }}
        >
          <IconButton
            onClick={handleZoomOut}
            disabled={zoom <= MIN_ZOOM}
            size='small'
            sx={{
              color: '#344266',
              '&:disabled': { opacity: 0.5 },
            }}
          >
            <Avatar
              src={ZoomOut}
              alt=''
              sx={{ width: '40px', height: '40px' }}
            />
          </IconButton>
          <IconButton
            onClick={handleZoomIn}
            disabled={zoom >= MAX_ZOOM}
            size='small'
            sx={{
              color: '#344266',
              '&:disabled': { opacity: 0.5 },
            }}
          >
            <Avatar
              src={ZoomIn}
              alt=''
              sx={{ width: '40px', height: '40px' }}
            />
          </IconButton>
        </Stack>
      )}
    </CustomDialog>
  );
};
